import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { searchIndex, searchCategories, popularSearches } from '@/mocks/search';
import SearchBar from '@/pages/search/components/SearchBar';
import ResultGroups, { type ResultGroup } from '@/pages/search/components/ResultGroups';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const groups = useMemo<ResultGroup[]>(() => {
    const kw = query.toLowerCase();
    const list = kw
      ? searchIndex.filter((entry) =>
          `${entry.title} ${entry.desc} ${entry.keywords} ${entry.category}`
            .toLowerCase()
            .includes(kw)
        )
      : searchIndex;

    return searchCategories
      .map((category) => ({
        category,
        items: list.filter((entry) => entry.category === category),
      }))
      .filter((group) => group.items.length > 0);
  }, [query]);

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  const handleSubmit = (value: string) => {
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SEO
        title={query ? `搜索「${query}」| 华辉数智` : '站内搜索 | 华辉数智'}
        description="在华辉数智官网搜索产品、解决方案、客户案例、九畴云市项目与公司资料，快速找到您需要的内容。"
        keywords="站内搜索,产品搜索,解决方案,客户案例,九畴云市"
        path="/search"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '站内搜索',
          description: '华辉数智官网站内搜索',
        }}
      />

      {/* 搜索首屏 */}
      <section className="relative w-full overflow-hidden bg-foreground-950 pt-28 pb-14 md:pt-36 md:pb-16">
        <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-primary-500/20 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-accent-500/15 blur-3xl pointer-events-none"></div>
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading font-bold text-background-50 text-3xl md:text-5xl">
              站内搜索
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-300">
              输入关键词，快速找到产品、解决方案、客户案例与公司资料。
            </p>
            <div className="mt-7 text-left">
              <SearchBar value={input} onChange={setInput} onSubmit={handleSubmit} />
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs text-foreground-400 mr-1">热门搜索</span>
              {popularSearches.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="px-3 py-1 rounded-full text-xs text-background-200 bg-background-50/10 border border-background-50/20 cursor-pointer transition-colors hover:bg-background-50/20 hover:text-background-50 whitespace-nowrap"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb items={[{ label: '站内搜索' }]} />

      <main className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          {query ? (
            total > 0 ? (
              <>
                <p className="mb-8 text-sm text-foreground-600">
                  关键词「<span className="font-semibold text-foreground-900">{query}</span>」共找到
                  <span className="mx-1 font-semibold text-primary-600">{total}</span>条结果
                </p>
                <ResultGroups groups={groups} />
              </>
            ) : (
              <div className="max-w-xl mx-auto text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
                </div>
                <h2 className="mt-5 font-heading font-bold text-xl text-foreground-950">
                  没有找到与「{query}」相关的内容
                </h2>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  试试更换关键词，或从下方热门搜索中挑选一个方向。
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                  {popularSearches.map((word) => (
                    <Link
                      key={word}
                      to={`/search?q=${encodeURIComponent(word)}`}
                      className="px-3 py-1.5 rounded-full text-sm text-secondary-900 bg-secondary-100 border border-secondary-200 cursor-pointer transition-colors hover:bg-secondary-200 whitespace-nowrap"
                    >
                      {word}
                    </Link>
                  ))}
                </div>
              </div>
            )
          ) : (
            <>
              <div className="mb-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  浏览全部内容
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  共 {total} 个内容入口，按分类浏览，或在上方输入关键词精确搜索。
                </p>
              </div>
              <ResultGroups groups={groups} />
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}